/* eslint-disable react/prop-types */
import { useEffect, useState, useCallback } from "react";
import Card from "@mui/material/Card";
import { formatDate } from "utils";
import SoftTypography from "components/SoftTypography";
import { JournalVoucher5Service } from "services/journal-voucher-5";
import { CompanyService } from "services/companyService";
import { formatVndIntegerValue } from "utils";
import toast from "react-hot-toast";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import SoftBox from "components/SoftBox";
import DataTable from "./DataTable";
import ActionCell from "./components/ActionCell";

const currentYear = String(new Date().getFullYear());
const currentMonth = String(new Date().getMonth() + 1);

const tableData = {
  columns: [
    { Header: "Số CT", accessor: "voucherNumber", align: "center" },
    { Header: "Ngày CT", accessor: "voucherDate", align: "center" },
    { Header: "Diễn giải", accessor: "description" },
    { Header: "TK Đối ứng", accessor: "counterpartAccounts", align: "center" },
    { Header: "Phát sinh Có TK 331", accessor: "creditAmount", align: "right" },
    { Header: "Thao tác", accessor: "action", sorted: false, align: "center" },
  ],
  rows: [],
};

function JournalVoucher5() {
  const [companyId, setCompanyId] = useState("");
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [dataTableState, setDataTableState] = useState(tableData);
  const [summary, setSummary] = useState({
    totalRows: 0,
    totalCreditAmount: 0,
  });
  const [filters, setFilters] = useState({
    year: currentYear,
    month: currentMonth,
    accountCode: "",
    searchBy: "",
  });
  const [query, setQuery] = useState({
    year: currentYear,
    month: currentMonth,
    accountCode: "",
    searchBy: "",
  });
  const [paginationData, setPaginationData] = useState({
    page: 1,
    count: 0,
    size: 10,
  });

  useEffect(() => {
    const init = async () => {
      try {
        const response = await CompanyService.getByAuth();
        const payload = response?.data || response || {};
        setCompanyId(payload?._id || "");
      } catch (error) {
        console.error("Lỗi lấy thông tin công ty:", error);
      }
    };
    init();
  }, []);

  const fetchAccounts = useCallback(async () => {
    if (!companyId) return;
    try {
      setAccountsLoading(true);
      const response = await JournalVoucher5Service.getAccounts({
        company: companyId,
      });
      const payload = response?.data || response || {};
      const items = payload.items || [];
      setAccounts(items);

      if (items.length > 0 && !filters.accountCode) {
        const firstAccount = items[0].accountCode;
        setFilters((prev) => ({ ...prev, accountCode: firstAccount }));
        setQuery((prev) => ({ ...prev, accountCode: firstAccount }));
      }
      return items;
    } catch (error) {
      console.error("Lỗi lấy danh sách tài khoản:", error);
      setAccounts([]);
      return [];
    } finally {
      setAccountsLoading(false);
    }
  }, [companyId, filters.accountCode]);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  const fetchData = useCallback(async () => {
    if (!companyId || !query.year || !query.month) {
      setDataTableState((prev) => ({ ...prev, rows: [] }));
      setSummary({ totalRows: 0, totalCreditAmount: 0 });
      setPaginationData((prev) => ({ ...prev, count: 0 }));
      return;
    }
    try {
      setLoading(true);
      const response = await JournalVoucher5Service.preview({
        company: companyId,
        year: query.year,
        month: query.month,
        accountCode: query.accountCode || undefined,
      });

      const payload = response?.data || response || {};
      const allRows = payload.rows || [];
      const normalizedSearch = (query.searchBy || "").trim().toLowerCase();
      const filteredRows = normalizedSearch
          ? allRows.filter((item) => {
            const counterpartText = (payload.debitAccountLabels || [])
                .filter((label, index) => Number(item?.debitValues?.[index] || 0) > 0)
                .join(" ");

            return [item.voucherNumber, item.voucherDate, item.description, counterpartText]
                .filter(Boolean)
                .join(" ")
                .toLowerCase()
                .includes(normalizedSearch);
          })
          : allRows;

      const params = new URLSearchParams({
        year: String(query.year),
        month: String(query.month),
        accountCode: String(query.accountCode),
        searchBy: String(query.searchBy || ""),
      }).toString();

      const rows = filteredRows.map((item) => {
        const counterpartAccounts = (payload.debitAccountLabels || [])
            .filter((label, index) => Number(item?.debitValues?.[index] || 0) > 0)
            .join(", ");

        return {
          voucherNumber: item.voucherNumber || "",
          voucherDate: item.voucherDate || "",
          description: item.description || "",
          counterpartAccounts: counterpartAccounts || "",
          creditAmount: formatVndIntegerValue(item.totalCredit331 || 0),
          action: (
              <ActionCell
                  item={{
                    viewLink: `/templates/journal-voucher-5/view?${params}`,
                  }}
              />
          ),
        };
      });

      setDataTableState((prev) => ({ ...prev, rows }));
      setSummary({
        totalRows: filteredRows.length,
        totalCreditAmount: (payload.totals?.totalCredit331 || 0),
      });
      setPaginationData((prev) => ({ ...prev, count: filteredRows.length }));
    } catch (error) {
      console.error("Lỗi lấy dữ liệu Nhật ký Chứng từ số 5:", error);
      setDataTableState((prev) => ({ ...prev, rows: [] }));
    } finally {
      setLoading(false);
    }
  }, [companyId, query]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSync = async () => {
    if (!companyId || !filters.year || !filters.month) {
      toast.error("Thiếu thông tin công ty hoặc thời gian");
      return;
    }
    try {
      setSyncing(true);
      const latestAccounts = (await fetchAccounts()) || [];
      const resolvedAccountCode =
        filters.accountCode || latestAccounts[0]?.accountCode || "";
      const nextFilters = {
        ...filters,
        accountCode: resolvedAccountCode,
      };

      setFilters(nextFilters);
      setPaginationData((prev) => ({ ...prev, page: 1 }));
      setQuery((prev) => ({ ...prev, ...nextFilters }));
      toast.success("Đã làm mới dữ liệu Nhật ký chứng từ số 5");
    } catch (error) {
      console.error("Lỗi đồng bộ dữ liệu:", error);
      toast.error(error?.response?.data?.message || "Đồng bộ dữ liệu thất bại");
    } finally {
      setSyncing(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox my={3}>
        <Card style={{ height: "100%" }}>
          <SoftBox p={3} display="flex" justifyContent="space-between" alignItems="center">
            <SoftTypography variant="h5" fontWeight="medium">
              NHẬT KÝ CHỨNG TỪ SỐ 5 (S04a5-DN)
            </SoftTypography>
          </SoftBox>
          <DataTable
            table={dataTableState}
            loading={loading}
            syncing={syncing}
            filters={filters}
            setFilters={setFilters}
            accounts={accounts}
            accountsLoading={accountsLoading}
            paginationData={paginationData}
            setPaginationData={setPaginationData}
            onFetchData={(newFilters) => {
              setPaginationData((prev) => ({ ...prev, page: 1 }));
              setQuery((prev) => ({ ...prev, ...newFilters }));
            }}
            onSync={handleSync}
            summary={summary}
          />
        </Card>
      </SoftBox>
    </DashboardLayout>
  );
}

export default JournalVoucher5;
